/**
 * State API - Application state detection and management
 * @module StateAPI
 */

export class StateAPI {
  constructor() {
    this.narrativeListeners = [];
    this.currentNarrativeState = null;
  }

  /**
   * Check if narrative mode is currently active
   * @returns {Promise<boolean>} True if narrative mode is active
   * @example
   * const isNarrative = await WavelengthHub.state.isNarrativeMode();
   * if (isNarrative) {
   *   console.log('Hide plugin UI during narrative');
   * }
   */
  async isNarrativeMode() {
    try {
      const response = await fetch('/api/state/narrative', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        return false;
      }
      
      const data = await response.json();
      this.currentNarrativeState = data.isActive ? data : null;
      return data.isActive || false;
    } catch (error) {
      console.warn('[StateAPI] Could not check narrative mode:', error);
      return false;
    }
  }

  /**
   * Get current narrative information if active
   * @returns {Promise<object|null>} Narrative info or null if not active
   * @example
   * const info = await WavelengthHub.state.getNarrativeInfo();
   * if (info) {
   *   console.log('Current narrative:', info.title);
   *   console.log('Chapter:', info.chapter);
   * }
   */
  async getNarrativeInfo() {
    const isActive = await this.isNarrativeMode();
    return isActive ? this.currentNarrativeState : null;
  }

  /**
   * Subscribe to narrative state changes
   * @param {Function} callback - Called when narrative state changes
   * @returns {Function} Unsubscribe function
   * @example
   * const unsubscribe = WavelengthHub.state.onNarrativeChange((isActive, info) => {
   *   if (isActive) {
   *     console.log('Narrative started:', info.title);
   *     hidePluginUI();
   *   } else {
   *     console.log('Narrative ended');
   *     showPluginUI();
   *   }
   * });
   * 
   * // Later, unsubscribe
   * unsubscribe();
   */
  onNarrativeChange(callback) {
    if (typeof callback !== 'function') {
      throw new Error('Callback must be a function');
    }

    this.narrativeListeners.push(callback);

    // Poll for changes every 2 seconds
    const pollInterval = setInterval(async () => {
      const isActive = await this.isNarrativeMode();
      const info = this.currentNarrativeState;
      
      // Notify all listeners
      this.narrativeListeners.forEach(listener => {
        try {
          listener(isActive, info);
        } catch (error) {
          console.error('[StateAPI] Listener error:', error);
        }
      });
    }, 2000);

    // Return unsubscribe function
    return () => {
      const index = this.narrativeListeners.indexOf(callback);
      if (index > -1) {
        this.narrativeListeners.splice(index, 1);
      }
      
      // Clear interval if no more listeners
      if (this.narrativeListeners.length === 0) {
        clearInterval(pollInterval);
      }
    };
  }

  /**
   * Get user preferences
   * @returns {Promise<object>} User preferences
   */
  async getPreferences() {
    try {
      const response = await fetch('/api/state/preferences', {
        credentials: 'include'
      });
      
      if (!response.ok) {
        return {};
      }
      
      return await response.json();
    } catch (error) {
      console.warn('[StateAPI] Could not get preferences:', error);
      return {};
    }
  }

  /**
   * Save user preference
   * @param {string} key - Preference key
   * @param {*} value - Preference value
   * @returns {Promise<boolean>} Success status
   */
  async savePreference(key, value) {
    try {
      const response = await fetch('/api/state/preferences', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ key, value })
      });
      
      return response.ok;
    } catch (error) {
      console.warn('[StateAPI] Could not save preference:', error);
      return false;
    }
  }
}
